import { useEffect, useMemo, useState } from "react";
import AppShell, { SectionCard, StatCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Wrench, ShieldCheck, ShoppingBag, CurrencyInr, Lightning, CheckCircle, X, Package } from "@phosphor-icons/react";
import { toast } from "sonner";

const err = (e) => toast.error(formatApiError(e?.response?.data?.detail));
const INR = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
const STATUS_COLOR = {
  pending_approval: "bg-amber-100 text-amber-800", submitted: "bg-blue-100 text-blue-700",
  approved: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-700",
  with_partner: "bg-teal-100 text-teal-700", in_progress: "bg-cyan-100 text-cyan-700",
  shipped: "bg-indigo-100 text-indigo-700",
  delivered: "bg-emerald-200 text-emerald-800", resolved: "bg-emerald-200 text-emerald-800",
};

export default function TechDeskAdmin() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [requests, setRequests] = useState([]);
  const [amc, setAmc] = useState([]);
  const [decision, setDecision] = useState(null);

  const load = async () => {
    try {
      const [o, r, a] = await Promise.all([
        api.get("/tech/admin/overview"),
        api.get("/tech/requests"),
        api.get("/tech/amc"),
      ]);
      setOverview(o.data); setRequests(r.data); setAmc(a.data);
    } catch (e) { err(e); }
  };
  useEffect(() => { load(); }, []);

  const t = overview?.totals || {};
  const pending = requests.filter(r => ["pending_approval","submitted"].includes(r.status));

  return (
    <AppShell title="TechDesk Admin">
      <div className="border-b border-zinc-200 mb-5 flex flex-wrap gap-1">
        {[["overview","Overview"], ["pending","Pending approvals · "+pending.length], ["all","All requests"], ["amc","AMC Vault"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`adm-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab===k ? "border-emerald-600 text-emerald-700" : "border-transparent text-zinc-600 hover:text-zinc-900"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && overview && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Pending approval" value={t.pending_approval} hint="Action required" testid="adm-stat-pending"/>
            <StatCard label="With TheGoodMen" value={t.with_partner} hint="In progress" testid="adm-stat-with-tgm"/>
            <StatCard label="Spend (this month)" value={INR(t.spend_mtd)} hint={`YTD ${INR(t.spend_ytd)}`} testid="adm-stat-spend"/>
            <StatCard label="AMC contracts" value={t.amc_active} hint={`${t.amc_expiring_30d} expiring in 30 days`} testid="adm-stat-amc"/>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Spend by category" subtitle="TheGoodMen pre-negotiated rates.">
              {(overview.spend_by_category || []).length === 0 ? (
                <div className="text-xs text-zinc-500 py-4">No spend yet this period.</div>
              ) : (() => {
                const max = Math.max(...overview.spend_by_category.map(c => c.amount), 1);
                return overview.spend_by_category.map(c => (
                  <div key={c.category} className="mb-2.5">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="capitalize">{c.category.replace(/_/g," ")}</span>
                      <span className="font-semibold tabular-nums">{INR(c.amount)}</span>
                    </div>
                    <div className="bg-zinc-100 rounded h-2"><div className="bg-teal-500 h-2 rounded" style={{ width: `${(c.amount/max)*100}%` }}/></div>
                  </div>
                ));
              })()}
            </SectionCard>

            <SectionCard title="Recent requests">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>By</TableHead><TableHead>Title</TableHead>
                  <TableHead>Amount</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(overview.recent_requests || []).slice(0,10).map(r => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs">{r.requester_name}</TableCell>
                      <TableCell className="text-xs font-medium truncate max-w-[180px]">{r.title}</TableCell>
                      <TableCell className="text-xs tabular-nums">{r.total_amount ? INR(r.total_amount) : "—"}</TableCell>
                      <TableCell><Badge className={STATUS_COLOR[r.status] || "bg-zinc-100"}>{r.status.replace(/_/g," ")}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SectionCard>
          </div>
        </div>
      )}

      {tab === "pending" && (
        <SectionCard title={`${pending.length} request(s) awaiting your decision`}>
          {pending.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center">All caught up. 🎉</div>}
          <div className="space-y-2">
            {pending.map(r => (
              <div key={r.id} className="border border-amber-200 bg-amber-50 rounded-lg p-3 flex items-center gap-3" data-testid={`pending-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{r.title}</div>
                  <div className="text-xs text-zinc-600 mt-0.5">
                    {r.requester_name} · {r.catalog_item_name && `${r.catalog_item_name} ×${r.qty} · `}
                    <span className="font-medium text-zinc-900 tabular-nums">{INR(r.total_amount)}</span>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => setDecision({ r, decision: "rejected" })} data-testid={`reject-${r.id}`}>Reject</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setDecision({ r, decision: "approved" })} data-testid={`approve-${r.id}`}>Approve</Button>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {tab === "all" && (
        <SectionCard title={`${requests.length} requests`}>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Created</TableHead><TableHead>By</TableHead><TableHead>Title</TableHead>
              <TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead>
              <TableHead>TGM #</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {requests.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-xs">{r.requester_name}</TableCell>
                  <TableCell className="text-xs font-medium">{r.title}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] capitalize">{r.request_type}</Badge></TableCell>
                  <TableCell className="text-xs tabular-nums">{r.total_amount ? INR(r.total_amount) : "—"}</TableCell>
                  <TableCell><Badge className={STATUS_COLOR[r.status] || "bg-zinc-100"}>{r.status.replace(/_/g," ")}</Badge></TableCell>
                  <TableCell className="text-xs font-mono">{r.osticket_number || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {tab === "amc" && (
        <SectionCard title={`AMC Vault · ${amc.length} contract(s)`} subtitle="Every device under an active maintenance contract. Auto-reminders 30/15/3 days before expiry.">
          {amc.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">No AMC contracts yet. Employees can opt into an AMC plan from the shop when buying or after delivery.</div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Asset</TableHead><TableHead>Plan</TableHead>
                <TableHead>Visits</TableHead><TableHead>Value/yr</TableHead>
                <TableHead>Ends</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {amc.map(a => (
                  <TableRow key={a.id}>
                    <TableCell className="text-sm">{a.asset_label}</TableCell>
                    <TableCell className="text-xs">{a.plan_name}</TableCell>
                    <TableCell className="text-xs tabular-nums">{a.used_visits}/{a.covered_visits}</TableCell>
                    <TableCell className="text-xs tabular-nums">{INR(a.annual_value)}</TableCell>
                    <TableCell className="text-xs">{a.end_date}</TableCell>
                    <TableCell><Badge className="bg-emerald-100 text-emerald-700">{a.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>
      )}

      {decision && <DecisionDialog d={decision} onClose={() => setDecision(null)} onDone={() => { setDecision(null); load(); }}/>}
    </AppShell>
  );
}

function DecisionDialog({ d, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/tech/requests/${d.r.id}/decide`, { decision: d.decision, note });
      toast.success(`Request ${d.decision}.${d.decision === "approved" ? " Pushed to TheGoodMen." : ""}`);
      onDone();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle>{d.decision === "approved" ? "Approve" : "Reject"} request</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-zinc-50 border border-zinc-200 rounded p-3 text-sm">
            <div className="font-semibold">{d.r.title}</div>
            <div className="text-xs text-zinc-600 mt-1">{d.r.requester_name} · {INR(d.r.total_amount)}</div>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Textarea rows={3} value={note} onChange={e=>setNote(e.target.value)} data-testid="decision-note"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className={d.decision === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
            data-testid="decision-confirm">
            {busy ? "Saving…" : `Confirm ${d.decision === "approved" ? "approval" : "rejection"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
